import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAuth } from "../auth/AuthContext";
import { exitImpersonationApi } from "../data/api";

/**
 * Persistent banner shown during a Super Admin "view-as-tenant" session
 * (Phase D). Names the acting administrator and the tenant, marks the session
 * read-only, and provides a one-click Exit that restores the super admin
 * session and records the exit in the audit trail.
 */
const ImpersonationBanner: React.FC = () => {
  const { impersonating, impersonationTenant, actingAdmin, tenantId, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!impersonating) return null;

  const exit = async () => {
    setBusy(true);
    const id = tenantId;
    // Restore the super-admin token FIRST (synchronously writes localStorage),
    // so the audit call below is authenticated as the super admin again.
    exitImpersonation();
    if (id != null) await exitImpersonationApi(id);
    navigate("/admin");
  };

  return (
    <Box sx={{
      position: "sticky", top: 0, zIndex: (t) => t.zIndex.appBar + 2,
      bgcolor: "#8a1c1c", color: "#fff", px: 2, py: 1,
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <VisibilityIcon fontSize="small" />
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Read-only tenant inspection
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.95 }}>
          Viewing <b>{impersonationTenant || "tenant"}</b>
          {actingAdmin ? <> · acting as <b>{actingAdmin}</b></> : null}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" color="inherit" disabled={busy}
          onClick={exit} sx={{ color: "#8a1c1c", bgcolor: "#fff" }}>
          Exit inspection
        </Button>
      </Stack>
    </Box>
  );
};

export default ImpersonationBanner;
